import { signupContext } from './signupContext';
import {
  AuthenticationDetails,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import {
  COGNITO_CLIENT_ID,
  COGNITO_DOMAIN,
  COGNITO_USER_POOL_ID,
  postLogoutUri,
  redirectUri,
} from './config';
import { createPkceChallenge, createState, rememberFlow } from './pkce';
import { decodeIdToken, StoredTokens } from './tokenStore';
import {
  forgetTrustedDevice,
  prepareTrustedDevice,
  rememberTrustedDevice,
} from './trustedDevice';

export class AuthError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
  }
}

const MESSAGES: Record<string, string> = {
  UsernameExistsException: 'An account with this email already exists. Try signing in instead.',
  NotAuthorizedException: 'Incorrect email or password.',
  UserNotFoundException: 'Incorrect email or password.',
  UserNotConfirmedException: 'This email is not verified yet. Enter the code we sent you.',
  CodeMismatchException: 'That code is not correct. Check it and try again.',
  ExpiredCodeException: 'That code has expired. Request a new one.',
  LimitExceededException: 'Too many attempts. Please wait a few minutes and try again.',
  InvalidPasswordException: 'Password does not meet the requirements.',
  TooManyRequestsException: 'Too many attempts. Please wait a few minutes and try again.',
};

const toAuthError = (error: unknown): AuthError => {
  if (error instanceof AuthError) {
    return error;
  }

  const raw = error as { name?: string; code?: string; message?: string } | null;
  const code = raw?.name || raw?.code || 'UnknownError';
  return new AuthError(code, MESSAGES[code] || raw?.message || 'Something went wrong. Please try again.');
};

const pool = (): CognitoUserPool => {
  if (!COGNITO_USER_POOL_ID || !COGNITO_CLIENT_ID) {
    throw new AuthError('NotConfigured', 'Accounts are not configured for this build.');
  }
  return new CognitoUserPool({
    UserPoolId: COGNITO_USER_POOL_ID,
    ClientId: COGNITO_CLIENT_ID,
  });
};

const cognitoUser = (username: string): CognitoUser =>
  new CognitoUser({ Username: username.trim().toLowerCase(), Pool: pool() });

let pendingTotp: { user: CognitoUser; email: string } | null = null;

const tokensFromSession = (session: CognitoUserSession): StoredTokens => ({
  idToken: session.getIdToken().getJwtToken(),
  accessToken: session.getAccessToken().getJwtToken(),
  refreshToken: session.getRefreshToken().getToken(),
});

export const signUp = (
  email: string,
  password: string,
  name: string
): Promise<{ userConfirmed: boolean }> =>
  new Promise((resolve, reject) => {
    const attributes = [new CognitoUserAttribute({ Name: 'email', Value: email.trim().toLowerCase() })];
    if (name.trim()) {
      attributes.push(new CognitoUserAttribute({ Name: 'name', Value: name.trim() }));
    }

    pool().signUp(email.trim().toLowerCase(), password, attributes, [], (error, result) => {
      if (error || !result) {
        reject(toAuthError(error));
        return;
      }
      resolve({ userConfirmed: result.userConfirmed === true });
    }, signupContext());
  });

export const confirmSignUp = (email: string, code: string): Promise<void> =>
  new Promise((resolve, reject) => {
    cognitoUser(email).confirmRegistration(code.trim(), true, (error) => {
      if (error) {
        reject(toAuthError(error));
        return;
      }
      resolve();
    }, signupContext());
  });

export const resendConfirmationCode = (email: string): Promise<void> =>
  new Promise((resolve, reject) => {
    cognitoUser(email).resendConfirmationCode((error) => {
      if (error) {
        reject(toAuthError(error));
        return;
      }
      resolve();
    });
  });

export const signIn = (email: string, password: string): Promise<StoredTokens> =>
  new Promise((resolve, reject) => {
    const normalizedEmail = email.trim().toLowerCase();
    prepareTrustedDevice(window.localStorage, COGNITO_CLIENT_ID, normalizedEmail);
    const user = cognitoUser(normalizedEmail);
    const details = new AuthenticationDetails({
      Username: normalizedEmail,
      Password: password,
    });

    user.authenticateUser(details, {
      onSuccess: (session) => {
        pendingTotp = null;
        resolve(tokensFromSession(session));
      },
      onFailure: (error) => {
        pendingTotp = null;
        reject(toAuthError(error));
      },
      totpRequired: () => {
        pendingTotp = { user, email: normalizedEmail };
        reject(new AuthError('TotpRequired', 'Enter the six-digit code from your authenticator app.'));
      },
      mfaRequired: () =>
        reject(
          new AuthError(
            'MfaNotSupported',
            'This account requires a second factor. Sign-in with MFA is not available on the website yet.'
          )
        ),
      newPasswordRequired: () =>
        reject(
          new AuthError(
            'NewPasswordRequired',
            'This account needs a new password. Use "Forgot password" to set one.'
          )
        ),
    });
  });

const setRememberedDevice = (user: CognitoUser, remembered: boolean): Promise<boolean> =>
  new Promise((resolve) => {
    const callbacks = {
      onSuccess: () => resolve(remembered),
      onFailure: () => resolve(false),
    };
    if (remembered) user.setDeviceStatusRemembered(callbacks);
    else user.setDeviceStatusNotRemembered(callbacks);
  });

export interface TotpSignInResult {
  tokens: StoredTokens;
  deviceRemembered: boolean;
}

export const confirmTotpSignIn = (code: string, rememberDevice: boolean): Promise<TotpSignInResult> =>
  new Promise((resolve, reject) => {
    const pending = pendingTotp;
    if (!pending) {
      reject(new AuthError('TotpSessionMissing', 'Start sign-in again before entering an authenticator code.'));
      return;
    }
    pending.user.sendMFACode(code.trim(), {
      onSuccess: async (session) => {
        const tokens = tokensFromSession(session);
        const deviceRemembered = await setRememberedDevice(pending.user, rememberDevice);
        if (rememberDevice && deviceRemembered) {
          rememberTrustedDevice(
            window.localStorage,
            COGNITO_CLIENT_ID,
            pending.email,
            pending.user.getUsername()
          );
        } else {
          forgetTrustedDevice(
            window.localStorage,
            COGNITO_CLIENT_ID,
            pending.email,
            pending.user.getUsername()
          );
        }
        pendingTotp = null;
        resolve({ tokens, deviceRemembered });
      },
      onFailure: (error) => reject(toAuthError(error)),
    }, 'SOFTWARE_TOKEN_MFA');
  });

const authenticatedUser = (tokens: StoredTokens): CognitoUser => {
  const username = decodeIdToken(tokens.idToken)?.['cognito:username'];
  if (!username) throw new AuthError('InvalidToken', 'The sign-in session could not be read.');
  const user = cognitoUser(username);
  user.setSignInUserSession(new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: tokens.idToken }),
    AccessToken: new CognitoAccessToken({ AccessToken: tokens.accessToken }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refreshToken }),
  }));
  return user;
};

export const totpEnabled = (tokens: StoredTokens): Promise<boolean> =>
  new Promise((resolve, reject) => {
    authenticatedUser(tokens).getUserData((error, data) => {
      if (error || !data) {
        reject(toAuthError(error));
        return;
      }
      resolve(data.UserMFASettingList?.includes('SOFTWARE_TOKEN_MFA') === true);
    });
  });

export const beginTotpSetup = (tokens: StoredTokens): Promise<string> =>
  new Promise((resolve, reject) => {
    authenticatedUser(tokens).associateSoftwareToken({
      associateSecretCode: resolve,
      onFailure: (error) => reject(toAuthError(error)),
    });
  });

export const confirmTotpSetup = (tokens: StoredTokens, code: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const user = authenticatedUser(tokens);
    user.verifySoftwareToken(code.trim(), 'iLand Studio', {
      onSuccess: () => {
        user.setUserMfaPreference(null, { Enabled: true, PreferredMfa: true }, (error) => {
          if (error) reject(toAuthError(error));
          else resolve();
        });
      },
      onFailure: (error) => reject(toAuthError(error)),
    });
  });

export const forgotPassword = (email: string): Promise<void> =>
  new Promise((resolve, reject) => {
    cognitoUser(email).forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (error) => reject(toAuthError(error)),
    });
  });

export const confirmForgotPassword = (
  email: string,
  code: string,
  password: string
): Promise<void> =>
  new Promise((resolve, reject) => {
    cognitoUser(email).confirmPassword(code.trim(), password, {
      onSuccess: () => resolve(),
      onFailure: (error) => reject(toAuthError(error)),
    });
  });

// Works for both password and Google sessions: Cognito issues the same kind of
// refresh token for either, and the username comes from the id token claims.
export const refreshTokens = (username: string, refreshToken: string): Promise<StoredTokens> =>
  new Promise((resolve, reject) => {
    cognitoUser(username).refreshSession(
      new CognitoRefreshToken({ RefreshToken: refreshToken }),
      (error, session: CognitoUserSession) => {
        if (error || !session) {
          reject(toAuthError(error));
          return;
        }
        resolve({
          idToken: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
          refreshToken: session.getRefreshToken()?.getToken() || refreshToken,
        });
      }
    );
  });

// Cognito's own API, authorised by the user's access token rather than AWS
// credentials, so the browser can change a password without a backend route.
export const changePassword = async (
  accessToken: string,
  previousPassword: string,
  proposedPassword: string
): Promise<void> => {
  const region = COGNITO_USER_POOL_ID.split('_')[0];
  if (!region) {
    throw new AuthError('NotConfigured', 'Accounts are not configured for this build.');
  }

  const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.ChangePassword',
    },
    body: JSON.stringify({
      AccessToken: accessToken,
      PreviousPassword: previousPassword,
      ProposedPassword: proposedPassword,
    }),
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { __type?: string; message?: string } | null;
    const code = String(body?.__type || 'UnknownError').split('#').pop() as string;
    if (code === 'NotAuthorizedException') {
      throw new AuthError(code, 'Your current password is not correct.');
    }
    throw toAuthError({ name: code, message: body?.message });
  }
};

export const revokeRefreshToken = async (refreshToken: string): Promise<void> => {
  if (!COGNITO_DOMAIN) {
    return;
  }

  await fetch(`${COGNITO_DOMAIN}/oauth2/revoke`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ token: refreshToken, client_id: COGNITO_CLIENT_ID }).toString(),
  }).catch(() => undefined);
};

export const hostedUiSignOutUrl = (): string | null => {
  if (!COGNITO_DOMAIN) {
    return null;
  }

  const params = new URLSearchParams({
    client_id: COGNITO_CLIENT_ID,
    logout_uri: postLogoutUri(),
  });
  return `${COGNITO_DOMAIN}/logout?${params.toString()}`;
};

export const startGoogleSignIn = async (returnTo: string): Promise<void> => {
  if (!COGNITO_DOMAIN) {
    throw new AuthError('NotConfigured', 'Google sign-in is not configured for this build.');
  }

  const { verifier, challenge } = await createPkceChallenge();
  const state = createState();
  rememberFlow(verifier, state, returnTo);

  const params = new URLSearchParams({
    identity_provider: 'Google',
    client_id: COGNITO_CLIENT_ID,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: redirectUri(),
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  window.location.assign(`${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`);
};

export const exchangeCodeForTokens = async (
  code: string,
  verifier: string
): Promise<StoredTokens> => {
  const response = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: COGNITO_CLIENT_ID,
      code,
      redirect_uri: redirectUri(),
      code_verifier: verifier,
    }).toString(),
  });

  if (!response.ok) {
    throw new AuthError('TokenExchangeFailed', 'Google sign-in could not be completed.');
  }

  const payload = (await response.json()) as {
    id_token?: string;
    access_token?: string;
    refresh_token?: string;
  };

  if (!payload.id_token || !payload.access_token || !payload.refresh_token) {
    throw new AuthError('TokenExchangeFailed', 'Google sign-in did not return a full session.');
  }

  return {
    idToken: payload.id_token,
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token,
  };
};
